/**
 * @fileoverview Upload button for the Files tab of a container list.
 *
 * Renders an "Upload" action button in the RecordTable toolbar's left action
 * group (via `options.customLeftToolbarChildren`, before the Move button) that
 * lets the user pick one or more local files and create FILE records from them
 * directly in the container the Files tab is scoped to (workspace / project /
 * bundle) — without leaving the list.
 *
 * The upload path mirrors the sidebar "Create → Upload file" flow
 * (components/Layout/Sidebar/components/CreateNew): read each file as a binary
 * string, base64-encode it, and dispatch START_CREATE_RESOURCE for the `file`
 * resource with the container location fields. Progress is tracked via the
 * shared Upload reducer (feeding the Topbar upload popover) and success/failure
 * is surfaced via NotificationService toasts. On completion the list is
 * refreshed via the `onUploaded` callback.
 *
 * Git/bundle containers add a step: when the target offers a choice — a
 * git-enabled container (per-upload `track_in_git` toggle, forced on + disabled
 * when the container forces tracking, plus a client-side over-limit guard that
 * blocks confirm when a git-tracked file would exceed the container's
 * `max_tracked_record_bytes`) or a bundle (`is_bundle_content` toggle) — a
 * confirm dialog is shown. Containers with no choice keep the no-dialog
 * immediate upload (§3 "no choice ⇒ no dialog"). The git/bundle-options UI and
 * its logic are shared with the Create-menu upload path via `UploadGitOptions`.
 *
 * @module UploadFileButton
 */

import { Buffer } from "buffer";
import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { IconCollection } from "components/Icon/IconCollection";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import { Popover } from "components/Popover/Popover";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { makeStyles } from "@mui/styles";
import {
    UploadGitOptions,
    useUploadContainerSettings,
    useUploadGitOptions,
} from "containers/Resources/components/CommonActions/MoveToLocation/UploadGitOptions";
import "containers/Resources/components/CommonActions/MoveToLocation/MoveToLocation.scss";
import { actions as uploadActions } from "containers/Upload/Upload.reducer";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useFilePicker } from "use-file-picker";
import { FileContent } from "use-file-picker/dist/interfaces";
import { v4 as uuidv4 } from "uuid";

/**
 * The container location a FILE record is created in. Mirrors the shape
 * produced by getLocationField (MoveToLocation): a workspace-root upload sets
 * both ids to null, a project/bundle upload sets the respective id.
 */
export type FileUploadLocation = {
    project_id?: string | null;
    bundle_id?: string | null;
};

type UploadFileButtonProps = {
    /** The container the uploaded FILE records are created in. */
    location: FileUploadLocation;
    /** Called after all uploads settle (e.g. to refresh the file list). */
    onUploaded?: () => void;
};

/**
 * Upload action button for the Files tab. Picks local files and creates a FILE
 * record per file in the given container location.
 */
export const UploadFileButton = ({ location, onUploaded }: UploadFileButtonProps) => {
    const dispatch = useDispatch();

    const [openFileSelector, { filesContent, loading, clear }] = useFilePicker({
        accept: "*",
        readAs: "BinaryString",
        multiple: true,
    });

    // Whether the git-options confirm dialog is open (only ever true for a
    // git-enabled target container — non-git containers upload immediately).
    const [dialogOpen, setDialogOpen] = useState(false);

    // The Files tab is scoped to a single container, so there is no location
    // picker — derive an option-shaped descriptor of the fixed target so the
    // shared git-settings resolver can look it up in the Redux plain-lists.
    const containerOption: AutocompleteOption | undefined = useMemo(() => {
        if (!location) return undefined;
        if (location.project_id) return { value: location.project_id, label: "", type: "project" };
        if (location.bundle_id) return { value: location.bundle_id, label: "", type: "bundle" };
        // Workspace root (both ids null).
        return { value: "workspace-root", label: "", type: "workspace" };
    }, [location]);

    const containerSettings = useUploadContainerSettings(containerOption);
    const fileSizes = useMemo(
        () => (filesContent ?? []).map((file) => file.content?.length ?? 0),
        [filesContent]
    );
    const gitOptions = useUploadGitOptions(containerOption?.value, containerSettings, fileSizes);

    /**
     * Upload the picked files: create one FILE record per file, track progress
     * via the Upload reducer, and surface success/failure via toasts. When the
     * target is git-enabled the dialog supplies `gitOptions.trackInGit`; for a
     * non-git target it is omitted so the backend applies the container default.
     */
    const upload = (
        fileInfos: FileContent[],
        uploadGitOptions?: { trackInGit?: boolean; isBundleContent?: boolean }
    ) => {
        if (!fileInfos || fileInfos.length <= 0) return;
        const uploadId = uuidv4();

        dispatch({
            type: uploadActions.START_UPLOAD,
            fileInfos,
            uploadId,
            recordType: "file",
        });

        const errors: Set<{ fileName: string; error: any }> = new Set();
        let successUploads: string[] = [];

        const promises = fileInfos.map((fileInfo) =>
            new Promise((resolve, reject) => {
                dispatch({
                    type: resourcesActions.START_CREATE_RESOURCE,
                    promise: { resolve, reject },
                    resourceName: "file",
                    variables: {
                        ...location,
                        name: fileInfo.name,
                        content: Buffer.from(fileInfo.content, "binary").toString("base64"),
                        // Only override track_in_git when the target is git-enabled
                        // and the dialog offered the choice; otherwise let the
                        // backend apply the container default.
                        ...(uploadGitOptions?.trackInGit !== undefined
                            ? { track_in_git: uploadGitOptions.trackInGit }
                            : {}),
                        // Only override is_bundle_content when the target is a
                        // bundle and the dialog offered the choice; otherwise let
                        // the backend apply the bundle default.
                        ...(uploadGitOptions?.isBundleContent !== undefined
                            ? { is_bundle_content: uploadGitOptions.isBundleContent }
                            : {}),
                    },
                });
            })
                .then((resource: any) => {
                    successUploads.push(fileInfo.name);
                    dispatch({
                        type: uploadActions.FILE_UPLOADED,
                        uploadId,
                        recordId: resource.id,
                        recordName: resource.name,
                        fileName: fileInfo.name,
                    });
                })
                .catch((error) => {
                    errors.add({ fileName: fileInfo.name, error });
                    dispatch({
                        uploadId,
                        type: uploadActions.FILE_UPLOAD_FAILED,
                        fileName: fileInfo.name,
                    });
                })
        );

        Promise.allSettled(promises).then(() => {
            dispatch({
                type: uploadActions.UPLOAD_FINISHED,
                uploadId,
            });

            if (errors.size > 0) {
                for (const error of errors) {
                    const displayError =
                        typeof error.error === "object"
                            ? `${
                                  (error.error as any).title
                                      ? `${(error.error as any).title}: `
                                      : ""
                              }${error.error.message}`
                            : error.error;
                    NotificationService.warn(
                        <ExpandableNotification
                            title={`${error.fileName} failed to upload`}
                            details={displayError}
                        />
                    );
                    successUploads = successUploads.filter((value) => value !== error.fileName);
                }
            }

            if (successUploads.length > 0) {
                NotificationService.success(
                    <ExpandableNotification
                        title="Upload finished"
                        subtitle={`${successUploads.length} file(s) uploaded`}
                        details={successUploads.join(", ")}
                    />
                );
            }

            onUploaded?.();
        });
    };

    // Once the file picker has read the selected files, either open the
    // git-options dialog (git-enabled target: a choice must be made) or upload
    // immediately (non-git target: no choice ⇒ no dialog).
    useEffect(() => {
        if (!filesContent || filesContent.length <= 0) return;
        if (gitOptions.showDialog) {
            setDialogOpen(true);
        } else {
            upload(filesContent);
            clear();
        }
        // upload()/clear() are stable enough for this effect; adding them (and
        // their captured deps) would re-run on every render — mirrors the
        // CreateNew pattern. showGitOptions is resolved from the fixed target
        // container, known before any file is picked.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesContent]);

    const closeDialog = () => {
        setDialogOpen(false);
        clear();
    };

    const confirmUpload = () => {
        if (gitOptions.blockConfirm) return;
        upload(filesContent, {
            ...(gitOptions.showGitOptions ? { trackInGit: gitOptions.trackInGit } : {}),
            ...(gitOptions.showBundleContent
                ? { isBundleContent: gitOptions.isBundleContent }
                : {}),
        });
        closeDialog();
    };

    const useStyles = makeStyles(() => ({
        rootStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
        paperStyle: { maxHeight: "600px", overflow: "visible" },
    }));
    const classes = useStyles();

    const fileCount = filesContent?.length ?? 0;

    return (
        <>
            <Button
                variation="dark"
                label="Upload"
                title="Upload file(s)"
                icon={IconCollection.uploadOutline}
                loading={loading}
                disabled={loading}
                onClick={openFileSelector}
            />
            <Popover
                isOpen={dialogOpen}
                classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
                rootClassName="moveToLocation__popover"
                contentClassName="moveToLocation__content"
                anchorReference="none"
                closeOnInsideClick={false}
                closeOnOutsideClick={false}
            >
                <div className="moveToLocation__header">
                    {fileCount > 1 ? `Upload ${fileCount} files` : "Upload file"}
                </div>
                <UploadGitOptions state={gitOptions} />
                <div className="button-container">
                    <CancelButton onClick={closeDialog} />
                    <Button
                        className="confirm-button"
                        variation="primary"
                        disabled={gitOptions.blockConfirm}
                        onClick={confirmUpload}
                        label="Upload"
                    />
                </div>
            </Popover>
        </>
    );
};

export default UploadFileButton;
